import { useCallback, useEffect, useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PageHero from '../components/ui/PageHero';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import CageForm from '../components/CageForm';
import {
  cagesApi,
  Cage,
  CageFilters,
  CageStats,
  CageStatus,
  CageType,
  CreateCageDto,
  FacingDirection,
  UpdateCageDto,
  CAGE_STATUS_LABELS,
  CAGE_TYPE_LABELS,
  FACING_LABELS,
  cageStatusColor,
} from '../api/cages';

export default function Cages() {
  const [cages, setCages] = useState<Cage[]>([]);
  const [stats, setStats] = useState<CageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CageFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selected, setSelected] = useState<Cage | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const [list, nextStats] = await Promise.all([
        cagesApi.list({ ...filters, search: searchTerm || undefined, limit: 200 }),
        cagesApi.stats(),
      ]);
      setCages(list.cages);
      setStats(nextStats);
    } catch (err) {
      console.error(err);
      setError('Failed to load cages');
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(timer);
  }, [load]);

  const handleCreate = async (data: CreateCageDto | UpdateCageDto) => {
    setFormLoading(true);
    try {
      await cagesApi.create(data as CreateCageDto);
      toast.success('Cage added');
      setShowCreate(false);
      await load();
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to add cage');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (data: CreateCageDto | UpdateCageDto) => {
    if (!selected) return;
    setFormLoading(true);
    try {
      await cagesApi.update(selected.id, data);
      toast.success('Cage updated');
      setShowEdit(false);
      setSelected(null);
      await load();
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to update cage');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await cagesApi.remove(selected.id);
      toast.success('Cage removed');
      setShowDelete(false);
      setSelected(null);
      await load();
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Failed to remove cage');
    }
  };

  if (loading && cages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHero
        title="Cages"
        subtitle="Housing catalog — numbers, dimensions, wire, nest boxes, and occupancy"
        actions={
          <button type="button" onClick={() => setShowCreate(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="h-5 w-5" />
            Add cage
          </button>
        }
      />

      {stats ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <ChartBarIcon className="h-5 w-5 text-cyan-600 mr-2" />
              <h2 className="text-lg font-semibold text-slate-900">Housing overview</h2>
            </div>
          </div>
          <div className="p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total', value: stats.total, color: 'text-slate-900' },
              { label: 'Active', value: stats.active, color: 'text-emerald-700' },
              { label: 'Occupied', value: stats.occupied, color: 'text-cyan-700' },
              { label: 'Empty', value: stats.empty, color: 'text-indigo-700' },
              { label: 'Maintenance', value: stats.maintenance, color: 'text-amber-700' },
            ].map((card) => (
              <div key={card.label} className="bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                <p className={`text-2xl font-semibold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1 max-w-lg">
            <div className="flex items-center w-full rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                placeholder="Search cages, wire, location…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button type="button" onClick={() => setViewMode('grid')} className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}>
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => setViewMode('table')} className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}>
                <ListBulletIcon className="h-5 w-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-slate-50/80"
            >
              <FunnelIcon className="h-5 w-5" />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {showFilters ? (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                <select
                  value={filters.cageType || ''}
                  onChange={(e) => setFilters({ ...filters, cageType: (e.target.value || undefined) as CageType | undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All types</option>
                  {Object.values(CageType).map((type) => (
                    <option key={type} value={type}>{CAGE_TYPE_LABELS[type]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters({ ...filters, status: (e.target.value || undefined) as CageStatus | undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All statuses</option>
                  {Object.values(CageStatus).map((status) => (
                    <option key={status} value={status}>{CAGE_STATUS_LABELS[status]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Facing</label>
                <select
                  value={filters.facingDirection || ''}
                  onChange={(e) => setFilters({ ...filters, facingDirection: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All directions</option>
                  {Object.values(FacingDirection).map((dir) => (
                    <option key={dir} value={dir}>{FACING_LABELS[dir]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Location</label>
                <input
                  type="text"
                  value={filters.location || ''}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value || undefined })}
                  placeholder="Filter by location…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button type="button" onClick={() => { setSearchTerm(''); setFilters({}); }} className="px-4 py-2 text-sm text-slate-600 hover:text-gray-800">
                Clear filters
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : null}

      {cages.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-slate-500">
          No cages yet. Add the first housing record to start selecting cage numbers across Birds, Breeding, and Chicks.
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {cages.map((cage) => (
            <div key={cage.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{cage.cageNumber}</h3>
                    <p className="text-sm text-slate-500">{cage.name || CAGE_TYPE_LABELS[cage.cageType]}</p>
                  </div>
                  <div className="flex space-x-1">
                    <button type="button" onClick={() => { setSelected(cage); setShowView(true); }} className="p-1 text-slate-400 hover:text-cyan-600">
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => { setSelected(cage); setShowEdit(true); }} className="p-1 text-slate-400 hover:text-green-600">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => { setSelected(cage); setShowDelete(true); }} className="p-1 text-slate-400 hover:text-red-600">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${cageStatusColor(cage.status)}`}>
                  {CAGE_STATUS_LABELS[cage.status]}
                </span>
                <dl className="mt-4 space-y-1 text-sm text-slate-600">
                  <div className="flex justify-between"><dt>Occupancy</dt><dd>{cage.occupancy}</dd></div>
                  <div className="flex justify-between"><dt>Volume</dt><dd>{cage.volumeLabel}</dd></div>
                  <div className="flex justify-between"><dt>Nest boxes</dt><dd>{cage.nestBoxCount}</dd></div>
                  {cage.wireType ? <div className="flex justify-between gap-2"><dt>Wire</dt><dd className="truncate">{cage.wireType}</dd></div> : null}
                  {cage.location ? <div className="flex justify-between"><dt>Location</dt><dd>{cage.location}</dd></div> : null}
                </dl>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50">
              <tr>
                {['Cage', 'Type', 'Status', 'Occupancy', 'Volume', 'Wire', 'Facing', 'Nest boxes', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cages.map((cage) => (
                <tr key={cage.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">
                    {cage.cageNumber}
                    {cage.name ? <div className="text-xs text-slate-500">{cage.name}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{CAGE_TYPE_LABELS[cage.cageType]}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${cageStatusColor(cage.status)}`}>
                      {CAGE_STATUS_LABELS[cage.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{cage.occupancy}</td>
                  <td className="px-4 py-3 text-sm">{cage.volumeLabel}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{cage.wireType || '—'}</td>
                  <td className="px-4 py-3 text-sm">{cage.facingDirection ? FACING_LABELS[cage.facingDirection] : '—'}</td>
                  <td className="px-4 py-3 text-sm">{cage.nestBoxCount}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => { setSelected(cage); setShowView(true); }} className="p-1 text-slate-400 hover:text-cyan-600"><EyeIcon className="h-4 w-4" /></button>
                    <button type="button" onClick={() => { setSelected(cage); setShowEdit(true); }} className="p-1 text-slate-400 hover:text-green-600"><PencilIcon className="h-4 w-4" /></button>
                    <button type="button" onClick={() => { setSelected(cage); setShowDelete(true); }} className="p-1 text-slate-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BrandModal open={showCreate} onClose={() => setShowCreate(false)} maxWidthClass="max-w-4xl">
        <BrandModalHeader title="Add cage" onClose={() => setShowCreate(false)} />
        <BrandModalBody>
          <CageForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} loading={formLoading} />
        </BrandModalBody>
      </BrandModal>

      <BrandModal open={showEdit && !!selected} onClose={() => { setShowEdit(false); setSelected(null); }} maxWidthClass="max-w-4xl">
        <BrandModalHeader title="Edit cage" onClose={() => { setShowEdit(false); setSelected(null); }} />
        <BrandModalBody>
          {selected ? (
            <CageForm cage={selected} onSubmit={handleUpdate} onCancel={() => { setShowEdit(false); setSelected(null); }} loading={formLoading} />
          ) : null}
        </BrandModalBody>
      </BrandModal>

      <BrandModal open={showView && !!selected} onClose={() => { setShowView(false); setSelected(null); }} maxWidthClass="max-w-3xl">
        <BrandModalHeader title={selected ? `Cage ${selected.cageNumber}` : 'Cage'} onClose={() => { setShowView(false); setSelected(null); }} />
        <BrandModalBody>
          {selected ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><p className="text-slate-500">Type</p><p className="text-slate-900">{CAGE_TYPE_LABELS[selected.cageType]}</p></div>
              <div><p className="text-slate-500">Status</p><p className="text-slate-900">{CAGE_STATUS_LABELS[selected.status]}</p></div>
              <div><p className="text-slate-500">Dimensions</p><p className="text-slate-900">{selected.length || '—'} × {selected.height || '—'} × {selected.breadth || '—'} {selected.dimensionUnit}</p></div>
              <div><p className="text-slate-500">Volume</p><p className="text-slate-900">{selected.volumeLabel}</p></div>
              <div><p className="text-slate-500">Wire</p><p className="text-slate-900">{selected.wireType || '—'}</p></div>
              <div><p className="text-slate-500">Facing</p><p className="text-slate-900">{selected.facingDirection ? FACING_LABELS[selected.facingDirection] : '—'}</p></div>
              <div><p className="text-slate-500">Location</p><p className="text-slate-900">{selected.location || '—'}</p></div>
              <div><p className="text-slate-500">Occupancy</p><p className="text-slate-900">{selected.occupancy} bird(s)</p></div>
              <div className="md:col-span-2">
                <p className="text-slate-500">Nest boxes</p>
                {selected.nestBoxes?.length ? (
                  <ul className="mt-1 list-disc pl-5 text-slate-900">
                    {selected.nestBoxes.map((box) => (
                      <li key={box.id}>{box.label}{box.type ? ` · ${box.type}` : ''}{box.position ? ` · ${box.position}` : ''}</li>
                    ))}
                  </ul>
                ) : <p className="text-slate-900">None</p>}
              </div>
              {selected.notes ? <div className="md:col-span-2"><p className="text-slate-500">Notes</p><p className="text-slate-900">{selected.notes}</p></div> : null}
            </div>
          ) : null}
        </BrandModalBody>
      </BrandModal>

      <BrandModal open={showDelete && !!selected} onClose={() => { setShowDelete(false); setSelected(null); }}>
        <BrandModalHeader title="Remove cage" onClose={() => { setShowDelete(false); setSelected(null); }} />
        <BrandModalBody>
          <p className="text-sm text-slate-600">
            Remove cage {selected?.cageNumber}? Occupied cages cannot be deleted until birds are moved.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button type="button" className="btn btn-secondary" onClick={() => { setShowDelete(false); setSelected(null); }}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>Remove</button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
}
